import { Collisions, GameStorageManager, Inject, List, ModelEvents, Singleton } from '@denishussar/html5-game-engine';
import {
    AppStateEnum,
    DialogTextEnum,
    DialogTypeTextEnum,
    EndScreenTextEnum,
    GameModel,
    GameStateEnum,
    QuestStateEnum,
    wasdEnum,
} from '../Models/GameModel';
import { MiniGameModel, MiniGameTypeEnum } from '../Models/MiniGameModel';
import { RoomModel } from '../Models/RoomModel';
import { FirebaseAPI } from '../../FirebaseAPI';
import DialogsConstants from '../Constants/DialogsConstants';
import Storage from '../Storage/Storage';
import { DisplayObject, Graphics } from 'pixi.js';

const HURRYUPTIMER = 90000;

@Singleton
export class GameLogic {
    _storage: Storage;

    constructor(
        @Inject private _model: GameModel,
        @Inject private _modelEvents: ModelEvents,
        @Inject private _collisions: Collisions,
        @Inject private _firebaseAPI: FirebaseAPI,
        @Inject private _storageManager: GameStorageManager,
    ) {
        this._storage = new Storage('Rosatom');
    }

    public init(): void {
        this.updatePosition();
        this.updateRooms();

        document.onvisibilitychange = () => {
            const unit = this._model.unitList.elementAt(0);

            unit.move[0] = 0;
            unit.move[1] = 0;
        };

        this._model.gameIsLoading = true;

        this.showMiniGame(MiniGameTypeEnum.departure);
        this._model.showHurryUpTimer = setTimeout(() => {
            this.showHurryUp();
        }, HURRYUPTIMER);
        this._firebaseAPI.setAnalyticsParams({ category: 'rosatom', gender: this.getGender() });
        this._firebaseAPI.trackGameStart();
    }

    private updateRooms() {
        let mapPos: [number, number] = [
            -this._model.pos[0],
            -this._model.pos[1],
        ];

        this._model.roomList.forEach(roomModel => {
            const isVisible = true; //roomModel.checkPos(mapPos);

            roomModel.environmentList.forEach(environment => {
                environment.visible = isVisible;
                environment.pos = [
                    roomModel.pos[0] + environment.initialPos[0],
                    roomModel.pos[1] + environment.initialPos[1],
                ];
            });

            roomModel.miniGameList.forEach(miniGame => {
                miniGame.visible = isVisible;
                miniGame.pos = [
                    roomModel.pos[0] + miniGame.initialPos[0],
                    roomModel.pos[1] + miniGame.initialPos[1],
                ];

            });

            roomModel.borderList.forEach(border => {
                border.visible = isVisible;
                border.pos = [
                    roomModel.pos[0] + border.initialPos[0],
                    roomModel.pos[1] + border.initialPos[1],
                ];
            });

            if (isVisible !== roomModel.visible) {
                roomModel.visible = isVisible;
            }
        });
    }

    public moveCursor(x: number, y: number): void {
    }

    public pressWASD(key: number): void {
        if ((this._model.gameState === GameStateEnum.none) && !this._model.showMiniGame) {
            this._model.wasdState[key] = 1;
            switch (key) {
                case wasdEnum.d:
                    this.unitMoveStart([1, 0]);
                    break;
                case wasdEnum.a:
                    this.unitMoveStart([-1, 0]);
                    break;
                case wasdEnum.w:
                    this.unitMoveStart([0, -1]);
                    break;
                case wasdEnum.s:
                    this.unitMoveStart([0, 1]);
                    break;
            }
        }
    }

    public resetAllMinigames() {
        switch (this._model.currMiniGameType) {
            case MiniGameTypeEnum.engine:
                this._model.EnginePowerUpModel.clear();
                break;
            case MiniGameTypeEnum.fuel:
                this._model.FuelModel.clear();
                break;
            case MiniGameTypeEnum.laser:
                this._model.LaserModel.clear();
                break;
            case MiniGameTypeEnum.ropes:

                this._model.RopesModel.clear();
                break;
        }
    }

    public releaseWASD(key: number): void {
        switch (key) {
            case wasdEnum.d:
                if (!this._model.wasdState[wasdEnum.a])
                    this.unitMoveStop([1, 0]);
                break;
            case wasdEnum.a:
                if (!this._model.wasdState[wasdEnum.d])
                    this.unitMoveStop([-1, 0]);
                break;
            case wasdEnum.w:
                if (!this._model.wasdState[wasdEnum.s])
                    this.unitMoveStop([0, -1]);
                break;
            case wasdEnum.s:
                if (!this._model.wasdState[wasdEnum.w])
                    this.unitMoveStop([0, 1]);
                break;
        }
        this._model.wasdState[key] = 0;
    }

    public unitMoveStart(direction: [number, number]) {
        if (!this._model.gamePad.visible) {
            return;
        }

        const unit = this._model.unitList.firstOrDefault(ch => !!ch.player);
        if (unit) {
            unit.move = [
                direction[0] || unit.move[0],
                direction[1] || unit.move[1],
            ];
            unit.visible = true;
        }
    }

    public unitMoveStop(direction: [number, number]) {
        let unit = this._model.unitList.firstOrDefault(ch => !!ch.player);
        if (unit) {
            unit.name = this._model.player.name;
            unit.move = [
                direction[0] ? 0 : unit.move[0],
                direction[1] ? 0 : unit.move[1],
            ];
        }
    }

    public mute(isMuted: boolean) {
        this._model.muteState = isMuted;
    }

    public setPlayDoneSound(play: boolean) {
        this._model.playDoneSound = play;
    }

    public setPlayLooseSound(play: boolean) {
        this._model.playLooseSound = play;
    }

    public addRoom(pos: [number, number], key: string): RoomModel {
        return this._model.addRoom(pos, key);
    }

    public reset(): void {
        this._model.reset();
        clearTimeout(this._model.showHurryUpTimer);

    }

    public update(frameRate: number): void {
        if (
            this._model.showMiniGame ||
            this._model.showMiniMap ||
            this._model.showTaskScreen
        ) {
            return;
        }

        if (this._model.player) {
            let unit = this._model.unitList.firstOrDefault(ch => !!ch.player);

            if (unit) {
                const iterations = 10;

                const moveX =
                    (unit.move[0] * frameRate * unit.speed) / iterations;
                const moveY =
                    (unit.move[1] * frameRate * unit.speed) / iterations;

                for (let i = 0; i < iterations; i++) {
                    const pos: [number, number] = [
                        unit.pos[0] + moveX,
                        unit.pos[1] + moveY,
                    ];

                    if (unit.move[0] || unit.move[1]) {
                        unit.walk = true;
                    } else {
                        unit.walk = false;
                    }

                    const result = this._collisions.createResult();
                    unit.collision.x = pos[0];
                    unit.collision.y = pos[1];
                    this._collisions.update();

                    const potentials = unit.collision.potentials();

                    for (const body of potentials) {
                        if (unit.collision.collides(body, result)) {
                            unit.collision.x -=
                                result.overlap * result.overlap_x;
                            unit.collision.y -=
                                result.overlap * result.overlap_y;
                        }
                    }

                    pos[0] = unit.collision.x;
                    pos[1] = unit.collision.y;

                    unit.pos = pos;

                    if (unit.move[0] || unit.move[1]) {
                        this.updatePosition();
                        this.updateRooms();
                    }
                }

                // MiniGame
                let miniGameModel = null;

                for (let i = 0; i < this._model.roomList.count(); i++) {
                    const room = this._model.roomList.elementAt(i);

                    for (let j = 0; j < room.miniGameList.count(); j++) {
                        const miniGame = room.miniGameList.elementAt(j);
                        const unitab = unit.aabb;


                        if (miniGame.type == MiniGameTypeEnum.laser) {
                            unitab[1] -= 100;
                        }
                        else
                        if (miniGame.type == MiniGameTypeEnum.fuel) {
                            unitab[1] += 100;
                        }
                        else
                        if (miniGame.type == MiniGameTypeEnum.wheel) {
                            unitab[1] += 200;
                        }
                        else
                        if (miniGame.type == MiniGameTypeEnum.engine) {
                            unitab[1] += 100;
                        }
                        if (miniGame.check(unitab)) {
                            miniGameModel = miniGame;
                            break;
                        }
                    }
                }

                // HACKS
                if (miniGameModel) {
                    if (this.isMiniGameComplete(MiniGameTypeEnum.engine)) {
                        if (miniGameModel.type !== MiniGameTypeEnum.ropes) {
                            miniGameModel = null;
                        }
                    } else {
                        if (miniGameModel.type === MiniGameTypeEnum.ropes) {
                            miniGameModel = null;
                        }
                    }
                }

                if (miniGameModel) {
                    if (miniGameModel.type === MiniGameTypeEnum.fuel || miniGameModel.type === MiniGameTypeEnum.laser) {
                        if (!this.foundTaskScreen()) {
                            miniGameModel = null;
                        }
                    }
                }

                if (
                    miniGameModel &&
                    miniGameModel.type !== this._model.currMiniGameType &&
                    !miniGameModel.completed
                ) {
                    this._model.currMiniGameType = miniGameModel.type;
                    this._model.currMiniGame = miniGameModel;
                    this._model.currMiniGame.selected = true;

                } else if (!miniGameModel &&
                    this._model.currMiniGameType !== MiniGameTypeEnum.none &&
                    this._model.currMiniGame &&
                    this._model.currMiniGame.selected) {
                    this._model.currMiniGameType = MiniGameTypeEnum.none;

                    if (this._model.currMiniGame) {
                        this._model.currMiniGame.selected = false;
                        this._model.currMiniGame = null;
                    }
                }
            }
        }
    }

    public updatePosition(): void {
        const unit = this._model.unitList.firstOrDefault();
        this._model.pos = [-unit.pos[0], -unit.pos[1]];
    }

    // Mini games logic
    public getMiniGames(): List<MiniGameModel> {
        let miniGameList = new List<MiniGameModel>();

        for (let i = 0; i < this._model.roomList.count(); i++) {
            miniGameList = miniGameList.concat(
                this._model.roomList.elementAt(i).miniGameList,
            );
        }

        return miniGameList;
    }

    public getMiniGameByType(miniGameType: MiniGameTypeEnum): MiniGameModel {
        return this.getMiniGames().first(o => o.type === miniGameType);
    }

    public getMiniGameWithQuestPointers(): MiniGameTypeEnum[] {
        return [
            MiniGameTypeEnum.anchor,
            MiniGameTypeEnum.boat,
            MiniGameTypeEnum.wheel,
            MiniGameTypeEnum.ropes,
            MiniGameTypeEnum.laser,
            MiniGameTypeEnum.fuel,
            MiniGameTypeEnum.engine,
            MiniGameTypeEnum.props,
        ];
    }

    public isMiniGameComplete(miniGameType: MiniGameTypeEnum): boolean {
        const miniGame = this.getMiniGameByType(miniGameType);
        return miniGame ? miniGame.completed && !miniGame.failed : this._model.doneMiniGames.includes(miniGameType);
    }

    public isMiniGamesComplete(miniGameTypes: MiniGameTypeEnum[]): boolean {
        for (let i = 0; i < miniGameTypes.length; i++) {
            if (!this.isMiniGameComplete(miniGameTypes[i])) {
                return false;
            }
        }
        return true;
    }

    public isMiniGameFailed(miniGameType: MiniGameTypeEnum): boolean {
        const miniGame = this.getMiniGameByType(miniGameType);
        return miniGame ? miniGame.failed : false;
    }

    public setMiniGameScreenPosition(
        miniGameType: MiniGameTypeEnum,
        position: [number, number],
    ): void {
        const miniGame = this.getMiniGameByType(miniGameType);

        if (miniGame) {
            if (
                position[0] !== miniGame.screenPos[0] ||
                position[1] !== miniGame.screenPos[1]
            ) {
                miniGame.screenPos = position;
            }
        }
    }

    public getUnitScreenPosition(): [number, number] {
        return this._model.unitList.first().screenPos;
    }

    public setSkinOptions(person: number, wear: number, hat: number): void {
        this._model.person = person;
        this._model.wear = wear;
        this._model.hat = hat;
    }

    public getSkinOptions(): { person: number; wear: number; hat: number } {
        return {
            person: this._model.person,
            wear: this._model.wear,
            hat: this._model.hat,
        };
    }

    public gamePadButtonA() {
        this.showMiniGame(this._model.currMiniGameType);
    }

    public showMiniGame(miniGameType: MiniGameTypeEnum = MiniGameTypeEnum.none): void {
        if (miniGameType !== MiniGameTypeEnum.none) {
            this._model.currMiniGameType = miniGameType;

            switch (miniGameType) {
                case MiniGameTypeEnum.departure:
                    this.showQuestScreen(QuestStateEnum.departure);
                    break;
                case MiniGameTypeEnum.anchor:
                    this.showQuestScreen(QuestStateEnum.anchor);
                    break;
                case MiniGameTypeEnum.boat:
                    this.showQuestScreen(QuestStateEnum.boat);
                    break;
                case MiniGameTypeEnum.wheel:
                    this.showQuestScreen(QuestStateEnum.wheel);
                    break;
                case MiniGameTypeEnum.find_boat:
                    this.showQuestScreen(QuestStateEnum.find_boat);
                    break;
                case MiniGameTypeEnum.task:
                    this.showTaskScreen();
                    break;

                case MiniGameTypeEnum.ropes:
                case MiniGameTypeEnum.laser:
                case MiniGameTypeEnum.fuel:
                case MiniGameTypeEnum.engine:
                case MiniGameTypeEnum.props:
                case MiniGameTypeEnum.rescue:
                case MiniGameTypeEnum.departure:
                    this._model.showMiniGame = true;
                    this._model.gamePad.showGamePad = false;
                    break;
            }

            this._modelEvents.dispatch(this._model, 'onMiniGameStart', miniGameType);
        }
    }

    public onMiniGameComplete(miniGameType: MiniGameTypeEnum, isLose: boolean = false): void {
        const miniGame = this.getMiniGameByType(miniGameType);
        if (miniGame) {
            miniGame.selected = false;
            miniGame.completed = true;
        }

        if (miniGameType != MiniGameTypeEnum.departure) {
            this._model.doneMiniGames.push(miniGameType);

            if (!this._model.failedMiniGames.includes(miniGameType)) {
                if (!isLose) {
                    this.setPlayDoneSound(true);
                }
                this._model.unitList.firstOrDefault().playWin = true;
            }
        }

        this._model.currMiniGame = null;
        this._model.currMiniGameType = MiniGameTypeEnum.none;

        this._model.showMiniGame = false;
        this._model.gamePad.showGamePad = true;

        this._modelEvents.dispatch(this._model, 'onMiniGameComplete', miniGameType);

        this.checkStory();
    }

    public onMiniGameFailed(miniGameType: MiniGameTypeEnum): void {
        const miniGame = this.getMiniGameByType(miniGameType);

        if (miniGame) {
            miniGame.failed = true;
        }

        this.setPlayLooseSound(true);

        this._model.failedMiniGames.push(miniGameType);

        switch (miniGameType) {
            case MiniGameTypeEnum.departure:
                this.hideQuestScreen();
                this.showEndScreen(EndScreenTextEnum.txt1, false);// final 1
                break;
        }
    }

    public setIsNewPlayer(value: boolean): void {
        this._model.showAfterloaderScreenNewPlayer = value;
    }

    // Screen
    public showAfterloaderScreen(isNewPlayer: boolean): void {
        this._model.showAfterloaderScreenNewPlayer = isNewPlayer;
        this._model.appState = AppStateEnum.afterloader;
    }

    public showMenuScreen(): void {
        this._model.appState = AppStateEnum.menu;
    }

    public showGameScreen(): void {
        this._model.appState = AppStateEnum.game;
    }

    public resetAppScreen(): void {
        this._model.appState = AppStateEnum.none;
    }

    // End Screen
    public showEndScreen(type: EndScreenTextEnum, victory: boolean = false): void {
        this._model.showEndType = type;
        this._model.showEndVictory = victory;
        this._model.appState = AppStateEnum.end;
        if (!victory) {
            this.setPlayLooseSound(true);
        }
    }

    public resetGameScreen(): void {
        this._model.showMiniMap = false;
        this._model.showMiniGame = false;
        this._model.showTaskScreen = false;
        this._model.showDialogScreen = false;
        this._model.showDialogScreenText = null;
        this._model.showTooltipScreen = false;
        this._model.gamePad.showGamePad = true;
        this._model.gameState = GameStateEnum.none;
    }

    public showCharacterScreen(): void {
        this._model.appState = AppStateEnum.character;
    }

    public showGameMiniMap(): void {
        this._model.showMiniMap = true;
        this._model.gameState = GameStateEnum.minimap;
    }

    // UIControllers
    public showQuestScreen(state: QuestStateEnum): void {
        this._model.questState = state;
        this._model.gameState = GameStateEnum.quest;
        this._model.showQuestScreen = true;
        this._model.gamePad.showGamePad = false;
    }

    public hideQuestScreen(): void {
        this._model.gameState = GameStateEnum.none;
        this._model.questState = QuestStateEnum.none;
        this._model.gamePad.showGamePad = true;
    }

    public getDialogText(dialogText: DialogTextEnum, dialogType: DialogTypeTextEnum, textIndex: number = 0): { titleText: string, buttonText: string } {
        return DialogsConstants[dialogText][dialogType][textIndex];
    }

    public showDialogScreen(dialogText: DialogTextEnum, dialogType: DialogTypeTextEnum, textIndex: number = 0, callback: () => void = null): void {
        const text = this.getDialogText(dialogText, dialogType, textIndex);
        // this._model.showDialogRect = rect;
        // HACK: rescue minigame
        if (dialogText === DialogTextEnum.rescue || dialogText === DialogTextEnum.props || dialogText === DialogTextEnum.ropes) {
            dialogType = DialogTypeTextEnum.start;
        }

        switch (dialogType) {
            case DialogTypeTextEnum.end:
                this.showTooltip(text.titleText, true);
                break;

            default:
                this._model.showDialogScreenText = text;
                this._model.showDialogScreenCallback = callback;
                this._model.showDialogScreen = true;
                //@ts-ignore
                if (text.rect) {
                    //@ts-ignore
                    const rect = text.rect as { x: number, y: number, width: number, height: number, color: number };
                    this._model.showDialogRect = new Graphics();
                    this._model.showDialogRect.beginFill(rect.color);
                    this._model.showDialogRect.drawRect(rect.x, rect.y, rect.width, rect.height);
                    this._model.showDialogRect.endFill();
                }
                else {
                    this._model.showDialogRect = null;
                }
        }

    }

    public hideDialogScreen(): void {
        this._model.EnginePowerUpModel.clear(); //dont delete this shit
        this._model.showDialogScreenText = null;
        this._model.showDialogScreenCallback = null;
        this._model.showDialogScreen = false;
    }

    public showTooltip(text: string, done: boolean = false): void {
        this._model.showTooltipScreenText = text;
        this._model.showTooltipDone = done;
        this._model.showTooltipScreen = true;
    }

    public hideTooltip(): void {
        this._model.showTooltipScreenText = '';
        this._model.showTooltipScreen = false;
    }

    public showHurryUp(): void {
        clearTimeout(this._model.showHurryUpTimer);
        if ((this._model.gameState == GameStateEnum.none) &&
            (this._model.currMiniGameType == MiniGameTypeEnum.none)) {
            if (this._model.showTooltipScreen) {
                this._model.showHurryUpTimer = setTimeout(() => {
                    this.showHurryUp();
                }, 100);
                return;
            }
            this.showTooltip(this.genHurryUp(), false);
            this._model.showHurryUpTimer = setTimeout(() => {
                this.showHurryUp();
            }, HURRYUPTIMER);
            return;
        }
        this._model.showHurryUpTimer = setTimeout(() => {
            this.showHurryUp();
        }, 500);
    }

    private genHurryUp() {
        if (Math.round(Math.random())) {
            return 'Моряк, время на исходе. Нужно быстрее разобраться с текущими делами.';
        }
        return 'Времени на подготовку судна все меньше, а надо еще столько всего успеть!';
    }

    public showInfoScreen(): void {
        this._model.gameState = GameStateEnum.info;
    }

    public hideInfoScreen(): void {
        this._model.gameState = GameStateEnum.none;
    }

    public showTaskScreen(): void {
        this._model.showTaskScreen = true;
        this._model.foundTaskScreen = true;
        this._model.gameState = GameStateEnum.task;
    }

    public hideTaskScreen(): void {
        this._model.showTaskScreen = false;
        this._model.gameState = GameStateEnum.none;
    }

    public foundTaskScreen(): boolean {
        return this._model.foundTaskScreen;
    }

    public setPtsName(ptsName: string): void {
        this._model.ptsName = ptsName;
    }

    public setPtsEmail(ptsEmail: string): void {
        this._model.ptsEmail = ptsEmail;
    }

    public async initDB(): Promise<void> {
        this._firebaseAPI.init();
    }

    public async initStorage(): Promise<void> {
        const currentId = this._storage.get('data');
        const currentName = this._storage.get('currentName');

        if (currentId !== null && typeof currentId !== 'undefined') {
            this._model.player.id = currentId;
            this._model.player.name = currentName;
            this.showAfterloaderScreen(false);

        } else {
            await this.showAfterloaderScreen(true);
            this._model.player.id = '';
        }
    }

    public async saveDB(name: string, email: string): Promise<void> {
        if (!this._model.player.id) {
            if (name && email) {
                console.log(name, email);

                const currentId = await this._firebaseAPI.save({
                    name: name,
                    email: email,
                });
                this._model.player.id = currentId;
                this._model.player.name = name;
                console.log(currentId);
                this._storage.save('data', currentId);
                this._storage.save('currentName', name);
            }
        } else {
            console.log(`Вы зарегестрированны под именем ${this._model.player.name} с ID = ${this._model.player.id}`);
        }
    }

    private getGender(): string {
        return this._model.person >= 2 ? 'female' : 'male';
    }

    private checkStory(): void {
        const isA = this._model.hat === 2;// helmet
        const isB = this._model.wear === 1;// blue wear

        const isQ = this.isMiniGamesComplete([
            MiniGameTypeEnum.boat,
            MiniGameTypeEnum.anchor,
            MiniGameTypeEnum.props,
            MiniGameTypeEnum.wheel,
        ]);

        const isL = this.isMiniGamesComplete([
            MiniGameTypeEnum.laser,
            MiniGameTypeEnum.fuel,
        ]);

        const isScene2 = this.isMiniGamesComplete([MiniGameTypeEnum.engine, MiniGameTypeEnum.ropes]);

        if (isScene2) {
            if (!isB) {
                this.showEndScreen(EndScreenTextEnum.txt2, false);// final 2

            } else {
                let currentMiniGameType = MiniGameTypeEnum.props;

                if (this.isMiniGamesComplete([MiniGameTypeEnum.props, MiniGameTypeEnum.find_boat, MiniGameTypeEnum.rescue])) {
                    currentMiniGameType = MiniGameTypeEnum.none;

                } else if (this.isMiniGamesComplete([MiniGameTypeEnum.props, MiniGameTypeEnum.find_boat])) {
                    currentMiniGameType = MiniGameTypeEnum.rescue;

                } else if (this.isMiniGamesComplete([MiniGameTypeEnum.props])) {
                    currentMiniGameType = MiniGameTypeEnum.find_boat;
                }

                if (currentMiniGameType !== MiniGameTypeEnum.none) {
                    this.showMiniGame(currentMiniGameType);
                } else {
                    if (isA && isQ && isL) { // final 3
                        this.showEndScreen(EndScreenTextEnum.txt1, true);

                    } else if (isA && isQ && !isL) { // final 4
                        this.showEndScreen(EndScreenTextEnum.txt2, true);

                    } else if (isA && !isQ) { // final 5
                        this.showEndScreen(EndScreenTextEnum.txt3, true);

                    } else if (!isA && isQ) { // final 6
                        this.showEndScreen(EndScreenTextEnum.txt3, false);

                    } else if (!isA && !isQ) { // final 7
                        this.showEndScreen(EndScreenTextEnum.txt4, false);
                    }
                }
            }
        }
    }
}
